/**
 * UniqueDropFormBody — drop-UNIQUE form body. The constraint name is required
 * on every supported engine (mssql / pgsql / oracle emit `DROP CONSTRAINT
 * name`; mysql emits `DROP INDEX name` — DSE2 dispatches per engine).
 * Cascade is exposed on pgsql + oracle.
 *
 * sqlite is gated upstream by `isDropUniqueConstraintSupported`; included as a
 * defensive runtime guard.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B3.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  commitDropUniqueConstraint,
  isCascadeSupported,
  validateDropUniqueConstraintForEngine,
} from '../../edit/edits/dropUniqueConstraint';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { FormEngineChip } from './FormEngineChip';

export interface UniqueDropFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function UniqueDropFormBody(props: UniqueDropFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const showCascade = isCascadeSupported(engine);
  const showMysqlNote = engine === 'mysql';

  const [constraintName, setConstraintName] = useState('');
  const [cascade, setCascade] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit() {
    setError(null);
    const args = {
      constraintName: constraintName.trim(),
      cascade: showCascade ? cascade : undefined,
    };
    const validationError = validateDropUniqueConstraintForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitDropUniqueConstraint(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Drop unique constraint</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          Constraint name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={constraintName}
          onChange={(e) => setConstraintName(e.target.value)}
          placeholder="uq_table_email"
          className="h-8 font-mono text-xs"
        />
      </div>

      {showMysqlNote ? (
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-mysql-drop-index-note`}
        >
          MySQL: UNIQUE constraints are stored as unique indexes. The synthesizer emits
          {' '}<code>DROP INDEX &lt;name&gt;</code> (universally supported on 5.7 + 8.x).
        </div>
      ) : null}

      {showCascade ? (
        <label className="flex items-center gap-2 text-xs" data-testid={`${testId}-cascade-label`}>
          <Checkbox
            checked={cascade}
            onCheckedChange={(checked) => setCascade(checked === true)}
            data-testid={`${testId}-cascade`}
          />
          <span>CASCADE — drop dependent constraints</span>
        </label>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button type="button" size="sm" variant="destructive" disabled={pending} onClick={() => void handleSubmit()} data-testid={`${testId}-submit`}>
          {pending ? 'Dropping…' : 'Drop'}
        </Button>
      </div>
    </div>
  );
}
